import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SpaceSearchBar from "./SpaceSearchBar";
import { AnalyticsEvents, AnalyticsProps, trackAnalyticsEvent } from "../utils/analytics";
import CountryPickerBanner from "./CountryPickerBanner";
import { detectUnsupportedCountry } from "../utils/locationUtils";

// Preload background images for faster loading
const preloadImages = () => {
  const heroImage = new Image();
  heroImage.src = require("../assets/img/home-hero.png");
  const overlayImage = new Image();
  overlayImage.src = require("../assets/img/heroOverlay.png");
};

const HeroSearchSection = ({
  title = "The Future of Work, Stay & Play",
  subtitle = "Discover flexible workspaces, unique accommodations, and curated experiences",
  showImage = true,
  onSearch = null,
  backgroundImage = "blue", // "blue" or "hero"
  isAuthenticated = false, // Pass true for authenticated users (e.g., start-booking)
  defaultSearchParams = {}, // Default search parameters to include
  animateHeroImage = true, // Control whether hero overlay image animates
  animateText = true, // Control whether title and subtitle animate
  animateBackground = true, // Control whether background animates
}) => {
  const history = useHistory();

  // Animation states
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [, setImagesLoaded] = useState(false);

  // Country picker banner state (mirrors mobile's _browseByCountryMode)
  const [showCountryBanner, setShowCountryBanner] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(undefined);

  // Preload images and set mounted state for animations
  useEffect(() => {
    // Preload images immediately when component mounts
    preloadImages();

    // Set mounted state for animations
    const mountedTimer = setTimeout(() => {
      setIsComponentMounted(true);
    }, 100);

    // Simulate image loading (in a real app, you'd track actual image load events)
    const imageLoadTimer = setTimeout(() => {
      setImagesLoaded(true);
    }, 200);

    return () => {
      clearTimeout(mountedTimer);
      clearTimeout(imageLoadTimer);
    };
  }, []);

  // Unauthenticated users rely on backend headers (x-country-code) or explicit user selection.
  useEffect(() => {
    if (isAuthenticated) return;
    let cancelled = false;

    detectUnsupportedCountry().then(result => {
      if (cancelled || !result) return;
      if (result.countryCode) {
        setDetectedCountry(result.country);
      }
    });

    return () => { cancelled = true; };
  }, [isAuthenticated]);


  const navigateToResults = searchFilters => {
    const targetRoute = isAuthenticated ? "/filterprops" : "/spaces";

    if (onSearch) {
      onSearch(searchFilters);
    }

    if (targetRoute === "/filterprops" || targetRoute === "/spaces") {
      sessionStorage.setItem("fromHeroSearch", "true");
    }

    history.push(targetRoute);
  };

  const handleSpaceBarSearch = ({ location, query, searchFilter, categoryId, subcategoryId, dateRange }) => {
    const trimmedLocation = (location || query || "").trim();
    const searchFilters = { ...defaultSearchParams };

    if (trimmedLocation) {
      const filterKey = searchFilter || "all";
      if (filterKey === "organizations") {
        searchFilters.hostOrgName = trimmedLocation;
      } else if (filterKey === "buildings") {
        searchFilters.buildingName = trimmedLocation;
      } else if (filterKey === "spaces") {
        searchFilters.spaceName = trimmedLocation;
      } else if (filterKey === "cities") {
        searchFilters.cityOrTown = trimmedLocation;
      } else if (filterKey === "countries") {
        searchFilters.country = trimmedLocation;
      } else {
        searchFilters.addressContains = trimmedLocation;
        searchFilters.buildingName = trimmedLocation;
        searchFilters.hostOrgName = trimmedLocation;
        searchFilters.spaceName = trimmedLocation;
        searchFilters.cityOrTown = trimmedLocation;
        searchFilters.country = trimmedLocation;
      }
      if (location) {
        searchFilters.addressContains = location.trim();
      }
    }

    if (searchFilter) {
      searchFilters.searchFilter = searchFilter;
    }

    if (categoryId) {
      searchFilters.categoryId = parseInt(categoryId, 10);
    }

    if (subcategoryId) {
      searchFilters.subCategoryId = parseInt(subcategoryId, 10);
    }

    localStorage.setItem("searchFilters", JSON.stringify(searchFilters));

    if (dateRange) {
      if (dateRange.startDate && dateRange.endDate) {
        const dateData = {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          startTime: dateRange.startDate,
          endTime: dateRange.endDate,
          startDateFormatted: new Date(dateRange.startDate).toLocaleDateString(),
          endDateFormatted: new Date(dateRange.endDate).toLocaleDateString(),
        };
        localStorage.setItem("searchDates", JSON.stringify(dateData));
      } else if (dateRange.specificDate && Array.isArray(dateRange.specificDate)) {
        localStorage.setItem("searchDates", JSON.stringify({ specificDate: dateRange.specificDate }));
      }
    } else {
      localStorage.removeItem("searchDates");
    }

    trackAnalyticsEvent(AnalyticsEvents.SEARCH_SUBMITTED, {
      [AnalyticsProps.LOCATION]: trimmedLocation || "any",
      [AnalyticsProps.NUM_FILTERS]: Object.keys(searchFilters).filter(k => searchFilters[k]).length,
      [AnalyticsProps.IS_AUTHENTICATED]: isAuthenticated,
    });

    navigateToResults(searchFilters);
  };

  return (
    <section
      className={`relative flex items-center py-8 sm:py-12 lg:py-0 ${isAuthenticated ? "h-full" : "min-h-screen"}`}
    >
      {/* Background - Blue gradient or Hero image */}
      <AnimatePresence>
        {backgroundImage === "hero" ? (
          <motion.div
            initial={animateBackground ? { opacity: 0 } : { opacity: 1 }}
            animate={animateBackground ? { opacity: 1 } : { opacity: 1 }}
            exit={animateBackground ? { opacity: 0 } : {}}
            transition={animateBackground ? { duration: 0.3, ease: "easeOut" } : {}}
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${require("../assets/img/home-hero.png")})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        ) : (
          <motion.div
            initial={animateBackground ? { opacity: 0 } : { opacity: 1 }}
            animate={animateBackground ? { opacity: 1 } : { opacity: 1 }}
            exit={animateBackground ? { opacity: 0 } : {}}
            transition={animateBackground ? { duration: 0.4, ease: "easeOut" } : {}}
            className="absolute inset-0"
          >
            {/* Blue background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600"></div>
            {/* Fade effect starting from middle */}
            <div className="absolute top-1/2 left-0 right-0 bottom-0 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content wrapper */}
      <div className="relative z-10 w-full text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full min-h-full flex items-center">
          <div
            className={`grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-center w-full ${backgroundImage === "hero" ? "justify-center" : ""}`}
          >
            {/* Left Image - 1/3 width (5 columns) - Only for blue background */}
            {showImage && backgroundImage !== "hero" && (
              <motion.div
                initial={animateHeroImage ? { opacity: 0, x: -50 } : { opacity: 1, x: 0 }}
                animate={animateHeroImage && isComponentMounted ? { opacity: 1, x: 0 } : { opacity: 1, x: 0 }}
                transition={animateHeroImage ? { duration: 1, ease: "easeOut", delay: 0.8 } : {}}
                className="order-1 lg:order-1 lg:col-span-5 flex justify-center lg:justify-start items-center lg:items-end h-full min-h-[400px] lg:min-h-[600px]"
              >
                <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                  <img
                    src={require("../assets/img/heroOverlay.png")}
                    alt="Professional workspace"
                    className="w-full h-auto object-contain object-bottom"
                  />
                </div>
              </motion.div>
            )}

            {/* Content - Full width for hero background, 2/3 width for blue background */}
            <div
              className={`order-2 lg:order-2 ${showImage && backgroundImage !== "hero" ? "lg:col-span-7" : "lg:col-span-12"} flex flex-col justify-center ${backgroundImage === "hero" ? "items-center text-center" : ""}`}
            >
              <div className={backgroundImage === "hero" ? "w-full max-w-6xl mx-auto box-border" : "w-full box-border"}>
                <motion.h1
                  initial={animateText ? { opacity: 0, y: 30 } : { opacity: 1, y: 0 }}
                  animate={animateText && isComponentMounted ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
                  transition={animateText ? { duration: 0.8, ease: "easeOut", delay: 0.2 } : {}}
                  className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight"
                >
                  {title}
                </motion.h1>
                <motion.p
                  initial={animateText ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
                  animate={animateText && isComponentMounted ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
                  transition={animateText ? { duration: 0.8, ease: "easeOut", delay: 0.4 } : {}}
                  className={`text-base sm:text-lg md:text-xl lg:text-lg xl:text-xl 2xl:text-2xl mb-6 sm:mb-8 lg:mb-10 text-blue-100 max-w-3xl leading-relaxed ${backgroundImage === "hero" ? "mx-auto" : ""}`}
                >
                  {subtitle}
                </motion.p>

                {/* Country picker banner — shown for international users (mirrors mobile browseByCountryMode) */}
                {!isAuthenticated && showCountryBanner && (
                  <CountryPickerBanner
                    detectedCountry={detectedCountry}
                    onCountrySelect={(_code) => {
                      // Country is persisted to localStorage by the banner itself;
                      // the page/service will pick it up on next API call.
                      setShowCountryBanner(false);
                    }}
                    onDismiss={() => setShowCountryBanner(false)}
                  />
                )}

                {/* Search Bar with Framer Motion shared layout animation */}
                <motion.div
                  layoutId="search-bar-hero-to-filter"
                  transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  className="w-full"
                >
                  <SpaceSearchBar
                    isAuthenticated={isAuthenticated}
                    onSearch={handleSpaceBarSearch}
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSearchSection;
